import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { usePublicData } from '../lib/hooks';
import { errorText } from '../lib/errors';
import type { DemoLogin } from '../lib/types';
import { ErrorNote } from './ui';

// A demo copy (staging, filled by scripts/demo-seed.mjs) lists one-tap accounts for every role in settings.demo_logins.
// The live site never has that row, so nothing here shows there.

export function useDemoLogins(): DemoLogin[] {
  return usePublicData().settings.data?.demo_logins ?? [];
}

function useDemoSignIn() {
  const { session, signOut } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = async (l: DemoLogin) => {
    setBusy(l.role);
    setError(null);
    try {
      if (session) await signOut();
      const { error: e } = await supabase.auth.signInWithPassword({ email: l.email, password: l.password });
      if (e) throw e;
      // A fresh load of the role's first page: nothing of the last role stays, and no redirect of the sign-in page
      // (or of a page the last role could see) gets there first.
      window.location.assign(l.to);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  return { go, busy, error };
}

/** "Try the demo" on the sign-in page: one tap signs in as that role. */
export function DemoLogins() {
  const logins = useDemoLogins();
  const { go, busy, error } = useDemoSignIn();
  if (!logins.length) return null;
  return (
    <section className="card demo-card" aria-label="Try the demo">
      <div className="stack" style={{ gap: 4 }}>
        <p className="app-eyebrow">Demo</p>
        <h2 className="demo-h">Try it as anyone, in one tap</h2>
        <p className="fine">Made-up families and orders, and Cashfree test payments: nothing here is real. Switch roles any time from the bar at the top.</p>
      </div>
      <div className="demo-roles">
        {logins.map((l) => (
          <button key={l.role} type="button" className="demo-role" disabled={!!busy} onClick={() => void go(l)}>
            <b>{busy === l.role ? 'Signing in…' : l.label}</b>
            <span>{l.who}</span>
            <span className="fine">{l.about}</span>
          </button>
        ))}
      </div>
      <ErrorNote error={error} />
    </section>
  );
}

/** The bar on every page of a demo copy: who you are, and another role in one tap. */
export function DemoBar() {
  const logins = useDemoLogins();
  const { session } = useAuth();
  const { pathname } = useLocation();
  const { go, busy, error } = useDemoSignIn();
  const [open, setOpen] = useState(false);
  // The TV board and the sign-in page (which has the full list) go without it.
  if (!logins.length || pathname === '/live' || pathname === '/signin') return null;
  const email = session?.user.email?.toLowerCase();
  const current = logins.find((l) => l.email === email);
  return (
    <div className="demo-bar no-print" role="region" aria-label="Demo">
      <span><b>Demo</b> · {current ? <>You are the <b>{current.label}</b> ({current.who})</> : session ? 'Signed in with your own email' : 'Made-up data, nothing is real'}</span>
      {session ? (
        <button type="button" className="link-btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? 'Close' : 'Switch role'}</button>
      ) : (
        <Link className="link-btn" to="/signin">Try it as any role ›</Link>
      )}
      {open && (
        <div className="demo-switch">
          {logins.filter((l) => l !== current).map((l) => (
            <button key={l.role} type="button" className="fchip" disabled={!!busy} onClick={() => { setOpen(false); void go(l); }}>
              {busy === l.role ? 'Signing in…' : l.label}
            </button>
          ))}
        </div>
      )}
      {error && <ErrorNote error={error} />}
    </div>
  );
}
